"use client";

import { ArrowRight, ArrowUpRight, Wrench } from "lucide-react";
import { motion, useReducedMotion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
export default function Services() {
  const prefersReducedMotion = useReducedMotion();
  const headerVariants = {
    hidden: {
      opacity: 0,
      y: 16
    },
    visible: (i: number) => ({
      opacity: 1,
      y: 0,
      transition: {
        duration: prefersReducedMotion ? 0 : 0.52,
        delay: prefersReducedMotion ? 0 : i * 0.09,
        ease: [0.22, 1, 0.36, 1] as [number, number, number, number]
      }
    })
  };
  const tradeServices = [{
    key: "appliances",
    label: "APPLIANCE REPAIR",
    sub: "Refrigerators \u00B7 Washers \u00B7 Dryers \u00B7 Dishwashers \u00B7 Ovens & Stoves",
    description: "Connect with independent repair specialists for kitchen and laundry appliances. Local pros troubleshoot cooling faults, water leaks, spin issues, and electrical ignition problems.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/appliance_repair_service.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/appliance_repair_service.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/appliance_repair_service.png-wvc-sizes",
    alt: "Independent technician diagnosing a kitchen refrigerator cooling component",
    to: "/ApplianceRepairPage",
    grid: "col-span-1 md:col-span-12 lg:col-span-7"
  }, {
    key: "hvac",
    label: "HVAC SERVICES",
    sub: "Heating \u00B7 Air Conditioning \u00B7 Heat Pumps \u00B7 System Diagnostics",
    description: "Match with local heating and cooling pros across metro Atlanta for seasonal tune-ups, compressor troubleshooting, airflow issues, and thermostat checks.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_system_repair.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_system_repair.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/hvac_system_repair.png-wvc-sizes",
    alt: "Local HVAC specialist inspecting an outdoor compressor unit",
    to: "/HVACServicesPage",
    grid: "col-span-1 md:col-span-6 lg:col-span-5"
  }, {
    key: "plumbing",
    label: "PLUMBING SERVICES",
    sub: "Pipes \u00B7 Drains \u00B7 Fixtures \u00B7 Garbage Disposals",
    description: "Request an independent plumber for clearing stubborn drain blockages, repairing pipe leaks, replacing fixtures, and servicing garbage disposal units.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumbing_pipe_repair.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumbing_pipe_repair.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/plumbing_pipe_repair.png-wvc-sizes",
    alt: "Independent plumber adjusting shutoff valve piping under a residential sink",
    to: "/PlumbingServicesPage",
    grid: "col-span-1 md:col-span-6 lg:col-span-4"
  }, {
    key: "electrical",
    label: "ELECTRICAL SERVICES",
    sub: "Breaker Panels \u00B7 Outlets \u00B7 Switches \u00B7 Wiring Inspections",
    description: "Find independent residential electricians to diagnose tripping breakers, add dedicated appliance circuits, replace outlets, and inspect home wiring systems.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrical_panel_work.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrical_panel_work.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/electrical_panel_work.webp-wvc-sizes",
    alt: "Local electrician testing circuit connections at a residential breaker panel",
    to: "/ElectricalServicesPage",
    grid: "col-span-1 md:col-span-6 lg:col-span-4"
  }, {
    key: "home-maintenance",
    label: "HOME MAINTENANCE",
    sub: "Preventive Inspections \u00B7 Seasonal Checkups \u00B7 System Upkeep",
    description: "Schedule regular preventive maintenance tasks and multi-point home evaluations to keep your building systems running smoothly across every season.",
    src: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_improvement_services.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_improvement_services.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1745538/img/home_improvement_services.webp-wvc-sizes",
    alt: "Independent tradesperson performing seasonal residential home maintenance tasks",
    to: "/HomeMaintenancePage",
    grid: "col-span-1 md:col-span-6 lg:col-span-4"
  }];
  const applianceCategories = [{
    name: "Refrigerator Repair",
    desc: "Cooling diagnostics, compressor issues, leaks, and ice maker troubleshooting.",
    to: "/RefrigeratorRepairPage"
  }, {
    name: "Washer Repair",
    desc: "Drainage failures, spin cycle issues, vibration, and water fill problems.",
    to: "/WasherRepairPage"
  }, {
    name: "Dryer Repair",
    desc: "No-heat symptoms, drum rotation issues, long dry cycles, and airflow checks.",
    to: "/DryerRepairPage"
  }, {
    name: "Dishwasher Repair",
    desc: "Spray arm issues, drainage errors, water leaking, and cleaning cycle faults.",
    to: "/DishwasherRepairPage"
  }, {
    name: "Oven & Stove Repair",
    desc: "Igniters, burner elements, temperature calibration, and door hinge seals.",
    to: "/OvenAndStoveRepairPage"
  }, {
    name: "Range Repair",
    desc: "Combined cooktop and oven troubleshooting, control board fixes, and burner care.",
    to: "/RangeRepairPage"
  }, {
    name: "Microwave Repair",
    desc: "Turntable motors, magnetron issues, door switches, and control panels.",
    to: "/MicrowaveRepairPage"
  }, {
    name: "Freezer Repair",
    desc: "Frost buildup, erratic temperatures, sealed system checks, and gasket seals.",
    to: "/FreezerRepairPage"
  }, {
    name: "Garbage Disposal Repair",
    desc: "Jammed impellers, motor humming, leak diagnosis, and replacement installs.",
    to: "/GarbageDisposalRepairPage"
  }];
  return <section data-section-id="32746" id="servicecategorygrid" className="bg-background py-24 md:py-32">
      <div className="mx-auto max-w-[1360px] px-5 sm:px-8 lg:px-16">
        {/* Main Service Categories Header */}
        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          <div className="md:col-span-10 lg:col-span-8">
            <motion.div custom={0} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.4
          }} variants={headerVariants} className="flex items-center gap-2">
              <span className="block h-[10px] w-[2px] bg-primary" aria-hidden="true" />
              <span className="font-default text-xs font-semibold uppercase text-primary" style={{
              letterSpacing: "0.22em"
            }}>
                Core Trade Hub
              </span>
              <span className="block h-[10px] w-[2px] bg-primary" aria-hidden="true" />
            </motion.div>

            <motion.h2 custom={1} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.4
          }} variants={headerVariants} className="font-default mt-5 uppercase text-foreground font-bold" style={{
            fontSize: "clamp(2.25rem, 5vw, 4.5rem)",
            lineHeight: 0.98,
            letterSpacing: "-0.01em"
          }}>
              Independent Trade Categories.
            </motion.h2>

            <motion.p custom={2} initial="hidden" whileInView="visible" viewport={{
            once: true,
            amount: 0.4
          }} variants={headerVariants} className="mt-6 text-base md:text-lg text-muted-foreground leading-relaxed max-w-2xl">
              Handy Heroes is a marketplace connecting Atlanta homeowners with independent trade specialists. Each contractor operates independently, inspects your equipment on site, and gives a direct quote before work starts.
            </motion.p>
          </div>
        </div>

        {/* 5 Core Service Cards Grid */}
        <div className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {tradeServices.map((service, index) => <div key={service.key} data-index={index} className={`${service.grid} group flex flex-col justify-between rounded-lg border border-border bg-card p-6 md:p-8 transition-colors hover:border-primary/50`}>
              <div>
                <div className="relative aspect-[16/9] w-full overflow-hidden rounded-md mb-6">
                  <img src={service.src} data-wvc-srcset={service.srcset} data-wvc-sizes={service.sizes} alt={service.alt} loading="lazy" decoding="async" className="h-full w-full object-cover transition-transform duration-300 group-hover:scale-[1.02]" />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent" aria-hidden="true" />
                  <div className="absolute bottom-3 left-3 right-3 flex items-center justify-between text-white">
                    <span className="text-xs font-mono uppercase tracking-wider bg-black/50 backdrop-blur-sm px-2.5 py-1 rounded">
                      {service.label}
                    </span>
                  </div>
                </div>

                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h3 className="font-default uppercase font-bold text-card-foreground text-xl md:text-2xl" style={{
                  letterSpacing: "-0.01em"
                }}>
                      {service.label}
                    </h3>
                    <p className="mt-1 font-mono text-xs uppercase tracking-wider text-primary">
                      {service.sub}
                    </p>
                  </div>
                </div>

                <p className="mt-4 text-sm md:text-base text-muted-foreground leading-relaxed">
                  {service.description}
                </p>
              </div>

              <div className="mt-6 pt-4 border-t border-border flex items-center justify-between">
                <Link to={service.to} className="inline-flex items-center gap-2 font-default text-sm font-semibold uppercase text-primary hover:underline underline-offset-4" style={{
              letterSpacing: "0.08em"
            }}>
                  <span>Explore {service.label}</span>
                  <ArrowRight className="h-4 w-4" aria-hidden="true" />
                </Link>
                <span className="text-xs font-mono text-muted-foreground">Local Pro Match</span>
              </div>
            </div>)}

          {/* Join Network Pro Card */}
          <div className="col-span-1 md:col-span-12 lg:col-span-12 rounded-lg bg-secondary text-secondary-foreground p-8 md:p-10 border border-border flex flex-col md:flex-row md:items-center justify-between gap-6">
            <div className="max-w-2xl">
              <span className="font-mono text-xs uppercase tracking-[0.16em] text-primary font-semibold">
                Contractor Network
              </span>
              <h3 className="mt-2 text-2xl md:text-3xl font-serif font-bold uppercase tracking-tight text-foreground">
                Are you an independent tradesperson in Atlanta?
              </h3>
              <p className="mt-2 text-sm md:text-base text-muted-foreground leading-relaxed">
                Apply to receive homeowner repair requests across Atlanta, Alpharetta, Marietta, Roswell, and Gwinnett. Independent pros set their own rates and schedule.
              </p>
            </div>
            <div className="flex flex-col sm:flex-row gap-3 shrink-0">
              <Button asChild className="h-11 px-6 rounded-lg uppercase tracking-wider font-semibold">
                <Link to="/JoinTheNetworkPage">Join Network</Link>
              </Button>
              <Button asChild variant="outline" className="h-11 px-6 rounded-lg uppercase tracking-wider font-semibold border-border">
                <Link to="/FindAProPage">Find a Pro</Link>
              </Button>
            </div>
          </div>
        </div>

        {/* Appliance Categories Hub Section */}
        <div className="mt-24 md:mt-32 pt-16 border-t border-border">
          <div className="flex flex-col md:flex-row md:items-end justify-between gap-6 mb-12">
            <div>
              <div className="flex items-center gap-2 mb-3">
                <Wrench className="h-4 w-4 text-primary" aria-hidden="true" />
                <span className="font-default text-xs font-semibold uppercase tracking-[0.22em] text-primary">
                  Appliance Coverage Directory
                </span>
              </div>
              <h2 className="font-default uppercase font-bold text-foreground text-3xl md:text-4xl" style={{
              letterSpacing: "-0.01em"
            }}>
                Appliance Repair by Unit Type
              </h2>
              <p className="mt-3 text-muted-foreground text-sm md:text-base max-w-xl">
                Browse independent appliance specialists for your exact unit, or visit our citywide guide for{" "}
                <Link to="/ApplianceRepairAtlantaPage" className="text-foreground underline decoration-primary underline-offset-4 hover:text-primary font-medium">
                  Appliance Repair in Atlanta, GA
                </Link>
                .
              </p>
            </div>

            <div className="shrink-0">
              <Button asChild variant="outline" className="rounded-full px-6 border-border font-medium text-sm">
                <Link to="/ApplianceRepairAtlantaPage" className="inline-flex items-center gap-2">
                  <span>Atlanta Appliance Guide</span>
                  <ArrowUpRight className="h-4 w-4 text-primary" aria-hidden="true" />
                </Link>
              </Button>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {applianceCategories.map((cat, idx) => <Link key={cat.to} to={cat.to} data-index={idx} className="group flex flex-col justify-between rounded-lg border border-border bg-card p-6 transition-all duration-200 hover:border-primary/60 hover:shadow-sm">
                <div>
                  <div className="flex items-center justify-between">
                    <h3 className="font-default text-lg font-bold uppercase text-card-foreground group-hover:text-primary transition-colors">
                      {cat.name}
                    </h3>
                    <ArrowUpRight className="h-4 w-4 text-muted-foreground group-hover:text-primary transition-colors" />
                  </div>
                  <p className="mt-2.5 text-xs md:text-sm text-muted-foreground leading-relaxed">
                    {cat.desc}
                  </p>
                </div>
                <div className="mt-5 pt-3 border-t border-border/60 flex items-center justify-between text-xs font-mono text-muted-foreground group-hover:text-foreground">
                  <span>View service details</span>
                  <span className="text-primary">&rarr;</span>
                </div>
              </Link>)}
          </div>
        </div>
      </div>
    </section>;
}